// StrategicPlanStepper.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  //Paper,
  List,
  ListItem,
  //ListItemIcon,
  ListItemText,
  //Divider,
  //Grid,
  Chip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Visibility as VisionIcon,
  SupervisorAccount as BoardIcon,
  Timeline as TimelineIcon,
  Security as CrisisIcon,
  TrendingUp as GrowthIcon,
} from '@mui/icons-material';
import HolographicStepper, { StepData } from '../common/HolographicStepper.tsx';
import GradientText from '../common/GradientText.tsx';
import EditableStepContent from '../common/EditableStepContent.tsx';

interface StrategicPlanStepperProps {
  companyName?: string;
  initialStep?: number;
  onComplete?: () => void;
  onSubmitRFP?: () => void;
}

const ContentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  background: 'rgba(26, 26, 46, 0.4)',
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  marginBottom: theme.spacing(2),
}));

const MetricChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  backgroundColor: 'rgba(0, 204, 153, 0.1)',
  color: '#00CC99',
  border: '1px solid rgba(0, 204, 153, 0.3)',
  '&:hover': {
    backgroundColor: 'rgba(0, 204, 153, 0.2)',
  },
}));

const StrategicPlanStepper: React.FC<StrategicPlanStepperProps> = ({
  companyName = "Company",
  initialStep = 0,
  onComplete,
  onSubmitRFP,
}) => {
  // Initialize state for costs
  const [stepCosts, setStepCosts] = useState({
    0: 2500, // Strategic Vision
    1: 1800, // Board & Stakeholder Alignment
    2: 2200, // Execution Roadmap
    3: 1500, // Risk & Crisis Management
    4: 2000, // Innovation & Growth
  });

  // Function to update a step's cost
  const handleCostChange = (stepIndex: number, newCost: number) => {
    setStepCosts(prev => ({
      ...prev,
      [stepIndex]: newCost
    }));
  };

  // Define steps with costs
  const strategicSteps: StepData[] = [
    {
      label: 'Strategic Vision',
      description: 'Define the long-term vision and mission for the company',
      icon: <VisionIcon />,
      cost: stepCosts[0],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Strategic Vision for {companyName}
          </GradientText>
          
          <EditableStepContent
            title="Vision & Mission Statement"
            cost={stepCosts[0]}
            onCostChange={(newCost) => handleCostChange(0, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Clearly articulate the company&apos;s long-term goals and the overarching mission that will drive future growth and innovation.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Strategic Objectives
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Market Leadership" 
                  secondary="Become the leading provider of AI-driven solutions in our industry. As measured by 20% market share growth in the next 3 years."
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
                <Box sx={{ ml: 1 }}>
                  <MetricChip label="20%" />
                </Box>
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Innovation Culture" 
                  secondary="Foster a culture of continuous innovation and adaptive strategy. As measured by 10% increase in new product development in the next 2 years."
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
                <Box sx={{ ml: 1 }}>
                  <MetricChip label="10%" />
                </Box>
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Global Expansion" 
                  secondary="Expand market reach and international presence. As measured by 20% increase in international revenue in the next 3 years."
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
                <Box sx={{ ml: 1 }}>
                  <MetricChip label="20%" />
                </Box>
              </ListItem>
            </List>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Board & Stakeholder Alignment',
      description: 'Establish alignment and clear communication with key stakeholders',
      icon: <BoardIcon />,
      cost: stepCosts[1],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Board & Stakeholder Alignment for {companyName}
          </GradientText>
          
          <EditableStepContent
            title="Key Messaging & Communication Plan"
            cost={stepCosts[1]}
            onCostChange={(newCost) => handleCostChange(1, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Develop clear, concise messaging for board members, investors, and key partners. Establish regular briefings and communication channels.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Stakeholder Engagement Strategy
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Regular Board Meetings" 
                  secondary="Schedule quarterly meetings to review strategic progress and challenges." 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Investor Updates" 
                  secondary="Provide monthly performance reports and strategic updates." 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Executive Communication" 
                  secondary="Establish a chain of communication between executive teams and frontline managers." 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
            </List>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Execution Roadmap',
      description: 'Outline a high-level strategic plan with milestones and timelines',
      icon: <TimelineIcon />,
      cost: stepCosts[2],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Execution Roadmap for {companyName}
          </GradientText>
          
          <EditableStepContent
            title="Major Milestones"
            cost={stepCosts[2]}
            onCostChange={(newCost) => handleCostChange(2, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Develop a Gantt chart or timeline outlining key initiatives, deliverables, and deadlines for the next 12 months.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Resource Allocation & Responsibilities
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="Strategic Initiatives" 
                  secondary="Assign roles and responsibilities for major projects and initiatives." 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Milestone Reviews" 
                  secondary="Plan quarterly reviews to assess progress and make necessary adjustments." 
                  secondaryTypographyProps={{ color: 'rgba(255, 255, 255, 0.7)' }}
                />
              </ListItem>
            </List>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Risk & Crisis Management',
      description: 'Identify, assess, and mitigate risks, and prepare crisis communication plans',
      icon: <CrisisIcon />,
      cost: stepCosts[3],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Risk & Crisis Management for {companyName}
          </GradientText>
          
          <EditableStepContent
            title="Risk Assessment Matrix"
            cost={stepCosts[3]}
            onCostChange={(newCost) => handleCostChange(3, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Develop a matrix to assess risks by likelihood and impact. Identify critical vulnerabilities in operations, market dynamics, and internal processes.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Crisis Communication Plan
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Outline protocols for swift, transparent communication during crises, including stakeholder notifications, media handling, and recovery strategies.
            </Typography>
          </ContentBox>
        </Box>
      )
    },
    {
      label: 'Innovation & Growth',
      description: 'Define growth initiatives and innovation strategies',
      icon: <GrowthIcon />,
      cost: stepCosts[4],
      content: (
        <Box>
          <GradientText gradient="primary" variant="h6" sx={{ mb: 2 }}>
            Innovation & Growth Strategy for {companyName}
          </GradientText>
          
          <EditableStepContent
            title="Growth Initiatives"
            cost={stepCosts[4]}
            onCostChange={(newCost) => handleCostChange(4, newCost)}
            content={
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                Identify and prioritize key growth drivers, including new market opportunities, strategic partnerships, and digital transformation projects.
              </Typography>
            }
          />
          
          <ContentBox>
            <Typography variant="subtitle1" sx={{ color: '#fff', mb: 1 }}>
              Innovation Roadmap
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Develop a framework for continuous innovation, including R&D investments, ideation sessions, and adoption of emerging technologies.
            </Typography>
          </ContentBox>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <HolographicStepper 
        steps={strategicSteps}
        initialStep={initialStep}
        onComplete={onComplete}
        onSubmitRFP={onSubmitRFP}
        orientation="horizontal"
        alternativeLabel={true}
      />
    </Box>
  );
};

export default StrategicPlanStepper;